import React from 'react';
import { range } from 'lodash-es';

import { Flex, FormField, IconPlusOutline, Input, Label, Paragraph, TextButton } from '@zillow/constellation';

import type { Section } from '../../../../models';
import { Field, SmokingPolicySection } from '../../../../models';
import { CenteredHR } from '../../../Widgets/CenteredHR';
import { SMOKING_POLICY } from '../../../../constants';
import type { HTMLElementEvent } from '../../../../models/Types';
import { useIndexedSection } from '../../../../hooks';

const MAXIMUM_SMOKING_AREAS = 5;

type Props = {
  section: Section;
  setSection: (section: SmokingPolicySection) => void;
  setSectionValues: (keyValuePairs: object) => void;
};

export default function SmokingAdditionalAreas({ section, setSection, setSectionValues }: Props) {
  const { onAddOne, onDeleteOne } = useIndexedSection({
    section,
    setSection,
    SectionClass: SmokingPolicySection,
  });

  function setCustomDescription(e: HTMLElementEvent<typeof Input>): void {
    const { index } = e.currentTarget.dataset;
    const fieldName = Field.getIndexedFieldName(SMOKING_POLICY.FIELD_NAME.CUSTOM_AREA_DESCRIPTION, Number(index));

    setSectionValues({ [fieldName]: e.currentTarget.value });
  }

  function renderArea(index: number): React.ReactNode {
    const descriptionFieldName = Field.getIndexedFieldName(SMOKING_POLICY.FIELD_NAME.CUSTOM_AREA_DESCRIPTION, index);

    const descriptionValue = section.getFieldValue(descriptionFieldName);

    return (
      <React.Fragment key={index}>
        {index !== MAXIMUM_SMOKING_AREAS && <CenteredHR />}
        <FormField
          label={
            <Flex display="flex" justifyContent="space-between" marginBottom="xs">
              <Label>Area #{index + 1}</Label>
              <TextButton
                aria-label={`Delete area #${index + 1}`}
                data-test="delete-link"
                data-index={index}
                onClick={onDeleteOne}
                fontType="bodySmallHeading"
              >
                Delete
              </TextButton>
            </Flex>
          }
          control={
            <Input
              marginBottom="sm"
              aria-label={`Area #${index + 1}`}
              data-index={index}
              data-test="input-description"
              value={descriptionValue}
              onChange={setCustomDescription}
            />
          }
        />
      </React.Fragment>
    );
  }

  const totalAreas = section.getTotalIndexes();

  return (
    <>
      {range(totalAreas).map((index: number) => (
        <React.Fragment key={index}>{renderArea(index)}</React.Fragment>
      ))}
      <Flex display="flex" alignItem="center" justifyContent="center">
        {totalAreas < MAXIMUM_SMOKING_AREAS ? (
          totalAreas >= 0 && (
            <TextButton onClick={onAddOne} icon={<IconPlusOutline />} marginTop="sm" data-test="add-another">
              Add another area
            </TextButton>
          )
        ) : (
          <Paragraph>You’ve added {MAXIMUM_SMOKING_AREAS} additional areas, which is the maximum allowed.</Paragraph>
        )}
      </Flex>
    </>
  );
}
